import { listOrgEnrollments } from "./enrollmentHelpers"
import { listCourses } from "./courseHelpers"
import { listPrograms } from "./programHelpers"
import { listPendingCredentialRequests } from "./credentialRequestHelpers"
import { listOrgOrders } from "./orderHelpers"
import { getApiHealthSummary } from "./apiMetricsHelpers"
import { listOrgInvitations, getInvitationDisplayStatus } from "./invitationHelpers"
import { toMillis } from "./dateRangeHelpers"

const DAY_MS = 86400000

export const computeAttentionData = async (orgId) => {
  const now = Date.now()

    const [
    { enrollments },
    { courses },
    { programs },
    { requests: pendingCredentials },
    { orders },
    apiHealth,
    { invitations },
  ] = await Promise.all([
    listOrgEnrollments(orgId),
    listCourses(orgId),
    listPrograms(orgId),
    listPendingCredentialRequests(orgId),
    listOrgOrders(orgId),
    getApiHealthSummary(orgId, 7),
    listOrgInvitations(orgId),
  ])

  // Inactive learners: distinct userIds with lastActivityAt older than 14 days
  const inactiveIds = new Set()
  enrollments?.forEach((e) => {
    const ms = toMillis(e?.lastActivityAt)
    if (e?.status === "active" && ms !== null && now - ms > 14 * DAY_MS) {
      inactiveIds.add(e?.userId)
    }
  })

  // Per-course completion vs org average (published courses only)
  const publishedCourses = courses?.filter((c) => c?.status === "published") || []
  const completionByCourse = publishedCourses?.map((course) => {
    const courseEnrollments = enrollments?.filter((e) => e?.courseId === course?.courseId) || []
    const avg = courseEnrollments?.length
      ? courseEnrollments?.reduce((sum, e) => sum + (e?.progressPercent || 0), 0) / courseEnrollments.length
      : 0
    return { courseId: course?.courseId, title: course?.title, avgCompletion: avg }
  })
  const orgAvgCompletion = completionByCourse?.length
    ? completionByCourse?.reduce((sum, c) => sum + c?.avgCompletion, 0) / completionByCourse.length
    : 0
  const belowAverageCourses = completionByCourse?.filter((c) => c?.avgCompletion < orgAvgCompletion) || []

  // Programs with a deadline in the next 30 days
  const upcomingPrograms = programs?.filter((p) => {
    const ms = toMillis(p?.deadlineAt)
    return ms !== null && ms >= now && ms <= now + 30 * DAY_MS
  }) || []

  // Pending payments, grouped by currency (most orgs will only ever have one)
  const pendingOrders = orders?.filter((o) => o?.status === "pending") || []
  const pendingByCurrency = {}
  pendingOrders?.forEach((o) => {
    const cur = o?.currency || "USD"
    pendingByCurrency[cur] = (pendingByCurrency[cur] || 0) + (o?.amount || 0)
  })
  const topCurrency = Object.keys(pendingByCurrency)?.sort((a, b) => pendingByCurrency[b] - pendingByCurrency[a])?.[0]

    // Invitations expiring within 7 days, still unaccepted â€” a real deadline
  // signal (the invitee still has time to act, but not much).
  const expiringInvitations = invitations?.filter((i) => {
    if (getInvitationDisplayStatus(i) !== "pending" && getInvitationDisplayStatus(i) !== "opened") return false
    const ms = toMillis(i?.expiresAt)
    return ms !== null && ms >= now && ms <= now + 7 * DAY_MS
  }) || []

  return {
    learners: { inactiveCount: inactiveIds.size },
    courses: { belowAverageCount: belowAverageCourses?.length, titles: belowAverageCourses?.map((c) => c?.title) },
    programs: { upcomingCount: upcomingPrograms?.length, titles: upcomingPrograms?.map((p) => p?.title) },
    credentials: { pendingCount: pendingCredentials?.length || 0 },
    payments: {
      pendingCount: pendingOrders?.length,
      pendingAmount: topCurrency ? pendingByCurrency?.[topCurrency] : 0,
      currency: topCurrency || "USD",
    },
    api: { errorRate: apiHealth?.errorRate || 0, totalRequests: apiHealth?.totalRequests || 0 },
    invitations: { expiringCount: expiringInvitations?.length || 0 },
  }
}